/**
 * Site-local time for site-bound datetimes (ADR-0047).
 *
 * A visit time is a wall-clock time *at the datacenter*. The site's IANA zone is resolved from the
 * site designator prefix (ICE01, DEN01, …) — every operating country is exactly one zone and the
 * prefix is the stable site key. Resolution fails closed: an unknown prefix yields `null`, never a
 * guess, so callers can refuse rather than silently fall back to the browser zone.
 *
 * "Wall-clock Date" below means a browser-local `Date` whose local components (year … minute) are
 * the intended wall-clock reading; that is what PrimeVue's DatePicker produces and consumes.
 */

export const SITE_TIME_ZONES: Readonly<Record<string, string>> = {
    ICE: 'Atlantic/Reykjavik',
    DEN: 'Europe/Copenhagen',
    SWE: 'Europe/Stockholm',
    NOR: 'Europe/Oslo',
    FIN: 'Europe/Helsinki',
};

export function resolveSiteTimeZone(siteName: string | null | undefined): string | null {
    if (!siteName) return null;
    const prefix = siteName.trim().slice(0, 3).toUpperCase();
    return SITE_TIME_ZONES[prefix] ?? null;
}

export function browserTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

interface WallClockParts {
    year: number;
    month: number; // 1-12
    day: number;
    hour: number;
    minute: number;
    second: number;
}

function wallClockPartsIn(instant: Date, timeZone: string): WallClockParts {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second'),
    };
}

/** UTC offset of `timeZone` at `instant`, in milliseconds (positive east of UTC). */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
    const p = wallClockPartsIn(instant, timeZone);
    const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Interpret the local components of `wallClock` as a reading in `timeZone`; return the instant. */
export function wallClockToInstant(wallClock: Date, timeZone: string): Date {
    const asIfUtc = Date.UTC(
        wallClock.getFullYear(),
        wallClock.getMonth(),
        wallClock.getDate(),
        wallClock.getHours(),
        wallClock.getMinutes(),
    );
    // Two passes settle the offset across a DST transition.
    const firstOffset = zoneOffsetMs(new Date(asIfUtc), timeZone);
    let instant = asIfUtc - firstOffset;
    const secondOffset = zoneOffsetMs(new Date(instant), timeZone);
    if (secondOffset !== firstOffset) instant = asIfUtc - secondOffset;
    return new Date(instant);
}

/** The wall-clock reading of `instant` in `timeZone`, as a browser-local Date for a DatePicker. */
export function instantToWallClock(instant: Date, timeZone: string): Date {
    const p = wallClockPartsIn(instant, timeZone);
    return new Date(p.year, p.month - 1, p.day, p.hour, p.minute);
}

/** Hours `timeZone` is ahead of `otherTimeZone` at `instant` (negative when behind). */
export function zoneOffsetDifferenceHours(instant: Date, timeZone: string, otherTimeZone: string): number {
    return (zoneOffsetMs(instant, timeZone) - zoneOffsetMs(instant, otherTimeZone)) / 3_600_000;
}

/**
 * False for a wall-clock reading `timeZone` skips — the spring-forward hour, e.g. 03:30 at FIN02 on
 * the last Sunday of March. Such a reading has no instant; `wallClockToInstant` would shift it.
 */
export function existsInZone(wallClock: Date, timeZone: string): boolean {
    const toMinute = new Date(wallClock);
    toMinute.setSeconds(0, 0);
    return instantToWallClock(wallClockToInstant(wallClock, timeZone), timeZone).getTime() === toMinute.getTime();
}
